import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSettings } from './settingsContext'

/* The Settings kit's non-component helpers (kit.tsx holds only components). */

/** Characters as OnlyFans counts them: an emoji is one, not its UTF-16 units. */
export function charCount(text: string): number {
  // Intl.Segmenter is newer than this project's TS lib; every supported browser has it.
  const Segmenter = (Intl as unknown as { Segmenter?: new (l?: string, o?: { granularity: 'grapheme' }) => { segment: (t: string) => Iterable<unknown> } }).Segmenter
  if (!Segmenter) return Array.from(text).length
  return Array.from(new Segmenter(undefined, { granularity: 'grapheme' }).segment(text)).length
}

/**
 * Load once (and again on `reload`). `data` stays until the next answer, so a
 * section can update it locally after a save with `set`.
 */
export function useLoad<T>(load: (signal: { live: boolean }) => Promise<T>, deps: readonly unknown[]) {
  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)
  const loader = useRef(load)
  loader.current = load
  useEffect(() => {
    const signal = { live: true }
    setLoading(true)
    setError('')
    loader
      .current(signal)
      .then((d) => {
        if (signal.live) setData(d)
      })
      .catch((e) => {
        if (signal.live) setError(e instanceof Error && e.message ? e.message : 'error')
      })
      .finally(() => {
        if (signal.live) setLoading(false)
      })
    return () => {
      signal.live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])
  const reload = useCallback(() => setNonce((n) => n + 1), [])
  return { data, error, loading, reload, set: setData }
}

/**
 * A setting that saves on change (OnlyFans' toggles save at once): shows the
 * new value right away and puts the old one back if OnlyFans refuses.
 */
export function useInstantSave() {
  const { say } = useSettings()
  const { t } = useTranslation('ofSettings')
  return useCallback(
    async <T,>(apply: (v: T) => void, prev: T, next: T, save: () => Promise<unknown>) => {
      apply(next)
      try {
        await save()
      } catch (e) {
        apply(prev)
        say(e instanceof Error && e.message ? e.message : t('error'))
      }
    },
    [say, t],
  )
}
